import { forwardRef, useImperativeHandle, useState } from "react";
import styled from "styled-components";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Modal = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  padding: 2rem;
  position: relative;
  color: var(--text-primary);

  h2 {
    margin-bottom: 1.5rem;
    color: var(--accent-color);
  }

  h3 {
    margin: 1.5rem 0 0.5rem;
    color: var(--text-primary);
  }

  p {
    margin-bottom: 1rem;
    color: var(--text-secondary);
    line-height: 1.6;
  }

  ul {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
    color: var(--text-secondary);
  }

  li {
    margin-bottom: 0.5rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  line-height: 1;

  &:hover {
    color: var(--text-primary);
  }
`;

export interface PrivacyPolicyRef {
  open: () => void;
  close: () => void;
}

export const PrivacyPolicy = forwardRef<PrivacyPolicyRef>((_, ref) => {
  const [isOpen, setIsOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  }));

  if (!isOpen) return null;

  return (
    <Overlay onClick={() => setIsOpen(false)}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={() => setIsOpen(false)}>×</CloseButton>
        <h2>Privacy Policy</h2>

        <p>Last updated: {new Date().toLocaleDateString()}</p>

        <h3>1. Introduction</h3>
        <p>
          Welcome to Kuack. We respect your privacy and are committed to protecting your personal data. This privacy
          policy will inform you as to how we look after your personal data when you visit our website and tell you
          about your privacy rights and how the law protects you.
        </p>

        <h3>2. The Data We Collect</h3>
        <p>We use Google Analytics to collect anonymous usage data. This may include:</p>
        <ul>
          <li>Pages you visit</li>
          <li>Time spent on pages</li>
          <li>Your approximate location (country/city level)</li>
          <li>Device and browser information</li>
        </ul>

        <h3>3. How We Use Your Data</h3>
        <p>
          We use this data solely to understand how visitors interact with our website and to improve our content and
          user experience. We do not sell this data to third parties.
        </p>

        <h3>4. Cookies</h3>
        <p>
          We use cookies to facilitate the collection of analytics data. You can choose to accept or reject these
          cookies through our cookie consent banner.
        </p>

        <h3>5. Contact</h3>
        <p>If you have any questions about this privacy policy, please contact us at info@kuack.io.</p>
      </Modal>
    </Overlay>
  );
});

PrivacyPolicy.displayName = "PrivacyPolicy";
